import './OrderLaunch.scss';

import { useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router';
import * as Sentry from '@sentry/react';

import Footer from '@components/Footer/Footer';
import { BRIDGE_BIO_CAMPAIGN_CODE } from '@constants/bridgeBio';
import { ROUTES } from '@constants/routes';
import { useHeaderHeight } from '@hooks/useHeaderHeight';

import { useOrderContext } from '../../hooks/useOrderContext';
import { DEFAULT_HEADLINE_TITLE } from './partnerConfig';
import { Benefits, FAQ, GetStarted, Headline, HowItWorks, PartnerIntro, WhyParticipate } from './sections';
import { mergeQueryParams } from './utils/mergeQueryParams';

export function OrderLaunch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { partnerConfig, hasInvalidPartner, isLoadingPartner } = useOrderContext();
  const headerHeight = useHeaderHeight();

  useEffect(() => {
    if (hasInvalidPartner) {
      const timer = setTimeout(() => {
        navigate('/error', { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hasInvalidPartner, partnerConfig, navigate]);

  const handleGetStarted = () => {
    if (!partnerConfig?.urlPath) return;

    const getStartedUrl = `/${ROUTES.PARTNERSHIP}/${partnerConfig.urlPath}/${ROUTES.ORDER_GET_STARTED}`;

    try {
      navigate(mergeQueryParams(getStartedUrl, searchParams));
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'OrderLaunch',
          action: 'handleGetStarted',
        },
        extra: {
          urlPath: partnerConfig.urlPath,
          partnerName: partnerConfig.name,
          campaignCode: partnerConfig.campaignCode,
        },
      });
      navigate(getStartedUrl);
    }
  };

  if (isLoadingPartner) {
    return (
      <div className='page_loading_spinner_wrapper text-primary'>
        <Spinner animation='border' />
      </div>
    );
  }

  // Show error/null if partner is invalid (will redirect via useEffect)
  if (!partnerConfig) {
    return null;
  }

  const isBridgeBio = partnerConfig.campaignCode === BRIDGE_BIO_CAMPAIGN_CODE;

  return (
    <>
      <div className='partner-order-launch' style={{ height: `calc(100vh - ${headerHeight}px)` }}>
        <main>
          <Headline
            description={partnerConfig.headlineDescription}
            showCasrcadeLockup={isBridgeBio}
            title={partnerConfig.headlineTitle || DEFAULT_HEADLINE_TITLE}
            onStart={handleGetStarted}
          />
          <PartnerIntro
            employerName={partnerConfig.displayName || partnerConfig.name}
            campaignCode={partnerConfig.campaignCode}
          />
          <WhyParticipate
            campaignCode={partnerConfig.campaignCode}
            onInviteFamily={() => navigate(ROUTES.FVT_WELCOME)}
            onOrderTesting={handleGetStarted}
            onReferPatient={() => navigate(ROUTES.BRIDGEBIO_REFERRAL)}
          />
          <Benefits />
          <HowItWorks />
          {!isBridgeBio && <GetStarted onStart={handleGetStarted} />}
          <FAQ />
        </main>
        <Footer />
      </div>
    </>
  );
}
